import { getOrder } from "@/api/backend/orders";
import { Button } from "@/components/Action/button";
import { resolveInsightDetails } from "@/pages/Order/Cart/utils";
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router";
import moment from "moment-timezone";
import type { InsightReportType } from "@/types/insight";
import { useExportInsightData } from "@/hooks/useExportInsightData";
import { toast } from "sonner";
import DisplayField from "@/components/DataDisplay/DisplayField";
import { getInsightDetailsByAudience } from "@/api/backend/insights/detail";
import { Inbox, Info } from "lucide-react";
import DynamicTable from "@/components/DataDisplay/Table";
import { LoadingText } from "@/components/Loading/Text";
import { Skeleton } from "@/components/Loading/skeleton";
import { isArrayWithItems } from "@/utils/functions/array";
import { OrderType } from "@/models/order";

function InsightDetail() {
  const { orderId, itemId } = useParams();
  const { exportInsightData, exportLoading } = useExportInsightData();

  // get the json config from the order details by id
  // print the attributes, details etc for each adudience, preload the multiple audience options in the tabs
  const [order, setOrder] = useState<OrderType>();
  const [selectedAudienceId, setSelectedAudienceId] = useState<string>("");
  const [reports, setReports] = useState<Record<string, InsightReportType>>({});
  const [reportLoadingStates, setReportLoadingStates] = useState<
    Record<string, boolean>
  >({});
  const [orderLoading, setOrderLoading] = useState<boolean>(false);

  const orderDetailsAbortController = useRef<AbortController | null>(null);
  const insightDetailsAbortController = useRef<AbortController | null>(null);

  const insight = useMemo(() => {
    if (!order || !itemId) return null;
    try {
      const foundInsight =
        order.items.find((item) => item.id === Number(itemId)) || null;
      if (!foundInsight) {
        console.error(
          `Insight item with ID ${itemId} not found in order ${orderId}`
        );
        return null;
      }

      console.log("foundInsight", foundInsight);
      const insightDetails = resolveInsightDetails(foundInsight);

      if (!insightDetails)
        throw new Error("Insight details could not be resolved.");

      const { country, timeframe, audiences, question } = insightDetails;

      return { country, timeframe, audiences, question };
    } catch (err) {
      console.log("Error resolving insight details:", err);
    }
  }, [order, orderId, itemId]);

  const formattedPurchaseDate = useMemo(() => {
    try {
      if (!order) return;
      if (!order?.date_paid) throw new Error("Could not resolve date_paid");

      const purchaseDate = order.date_paid;

      return moment.utc(purchaseDate).local().format("MMMM Do, YYYY");
    } catch (error) {
      console.log(error);
      return "!N/A";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.date_paid]);

  const selectedAudience = useMemo(() => {
    try {
      if (!insight) return;
      if (!Array.isArray(insight?.audiences) || insight.audiences.length < 1)
        throw new Error("No audiences to evaluate");
      if (!selectedAudienceId) throw new Error("No audience is selected");

      return insight.audiences.filter(
        (a) => a.audience_id === selectedAudienceId
      )[0];
    } catch (error) {
      console.log(error);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudienceId, insight?.audiences]);

  const currentReport = useMemo(() => {
    return selectedAudienceId ? reports[selectedAudienceId] : null;
  }, [selectedAudienceId, reports]);

  const currentReportMatrix = useMemo(() => {
    if (!currentReport) return null;
    try {
      const matrix = currentReport.levels.map((level) => {
        const audience = level.audiences?.[0];
        const audienceCount = audience?.count || {};

        return {
          variable: level.name,
          percent: audienceCount.percentage,
          sampleSize: audienceCount.audience_sample_size,
        };
      });

      return matrix;
    } catch (error) {
      console.log(error);
      return null;
    }
  }, [currentReport]);

  const isCurrentReportLoading = useMemo(() => {
    return selectedAudienceId ? reportLoadingStates[selectedAudienceId] : false;
  }, [selectedAudienceId, reportLoadingStates]);

  const getOrderDetails = async () => {
    if (orderDetailsAbortController.current) {
      orderDetailsAbortController.current.abort();
    }

    orderDetailsAbortController.current = new AbortController();

    // This will return the entire order of which this insight item is a part of. We only need the insight (produt) that matches the item_id
    try {
      const response = await getOrder({
        aController: orderDetailsAbortController.current,
        order_id: orderId as string,
      });

      return response;
    } catch (err) {
      console.log(err);
    }
  };

  const hydrateOrder = async () => {
    try {
      setOrderLoading(true);
      const response = await getOrderDetails();
      if (!response) {
        throw new Error("Failed to hydrate order");
      }
      if (response.status === "aborted") {
        throw new Error("Order hydration aborted");
      }

      if (response.status === "error") {
        throw new Error(response?.error?.error.message);
      }

      setOrder(response.data);
    } catch (err) {
      toast.error("Failed to hydrate order.");
      console.log(err);
    } finally {
      setOrderLoading(false);
    }
  };

  const fetchInsightDetails = async () => {
    if (!selectedAudienceId) return;

    if (reports[selectedAudienceId]) return;

    if (reportLoadingStates[selectedAudienceId]) return;

    setReportLoadingStates((prev) => ({ ...prev, [selectedAudienceId]: true }));

    if (insightDetailsAbortController.current)
      insightDetailsAbortController.current.abort();

    insightDetailsAbortController.current = new AbortController();

    try {
      const response = await getInsightDetailsByAudience({
        aController: insightDetailsAbortController.current,
        orderId: orderId as string,
        audienceId: selectedAudienceId,
        itemId: parseInt(itemId as string),
      });

      if (response?.status === "aborted") return;

      if (response?.status === "error") {
        setReportLoadingStates((prev) => ({
          ...prev,
          [selectedAudienceId]: false,
        }));
        toast.error(response.error?.message || "Failed to fetch report");
        return;
      }

      if (response?.error === true || response?.details) {
        setReportLoadingStates((prev) => ({
          ...prev,
          [selectedAudienceId]: false,
        }));
        toast.error(response.message || "No data available for this audience");
        return;
      }

      setReports((prev) => ({ ...prev, [selectedAudienceId]: response }));
    } catch (error: any) {
      toast.error(error?.message || "Error fetching report");
      console.log(error);
    } finally {
      setReportLoadingStates((prev) => ({
        ...prev,
        [selectedAudienceId]: false,
      }));
    }
  };

  const handleExportDataClick = () => {
    if (order && order.items[0])
      exportInsightData({ order, item: order.items[0] });
  };

  useEffect(() => {
    if (!orderId) {
      console.error("No order ID found in query parameters");
      return;
    }

    hydrateOrder();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!insight?.audiences) return;
    try {
      if (Array.isArray(insight?.audiences) && insight?.audiences.length > 0) {
        setSelectedAudienceId(insight.audiences[0].audience_id);
      }
    } catch (err) {
      console.error("Error processing audiences:", err);
    }
  }, [insight?.audiences]);

  useEffect(() => {
    if (orderId && itemId && selectedAudienceId) fetchInsightDetails();
  }, [orderId, itemId, selectedAudienceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (orderDetailsAbortController.current) {
        orderDetailsAbortController.current.abort();
      }
      if (insightDetailsAbortController.current) {
        insightDetailsAbortController.current.abort();
      }
    };
  }, []);

  return (
    <div className="wrap p-2 md:p-6 pb-12">
      <h1 className="h2">Insight Detail</h1>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between px-2">
            <span>
              <span className="h5 uppercase opacity-50 block !mb-0">
                {orderLoading ? (
                  <LoadingText inline={true} size="md" />
                ) : (
                  "Date Purchased:"
                )}
              </span>
              <span className="h5 block m-0 !mb-0 !font-normal">
                {orderLoading ? (
                  <LoadingText inline={false} size="sm" />
                ) : (
                  formattedPurchaseDate
                )}
              </span>
            </span>
            <span>
              {orderLoading ? (
                <Skeleton className="h-10 w-20" />
              ) : (
                <Button
                  variant="default"
                  onClick={handleExportDataClick}
                  disabled={exportLoading.includes(`${orderId}-${itemId}`)}
                  className="cursor-pointer"
                >
                  {exportLoading.includes(`${orderId}-${itemId}`)
                    ? "Loading..."
                    : "Export Data"}
                </Button>
              )}
            </span>
          </div>

          <div className="bg-[var(--neutral-10)] p-4">
            {orderLoading ? (
              <LoadingText size="lg" inline={false} />
            ) : (
              <h3 className="h2 !m-0 !font-normal">Inputs Summary</h3>
            )}
          </div>

          <div className="flex flex-col md:flex-row gap-2">
            <div className="bg-[var(--neutral-10)] p-4 flex-1 flex flex-col gap-3 rounded-xs">
              {orderLoading ? (
                <LoadingText size="lg" inline={false} />
              ) : (
                <h4 className="h3 !m-0 !font-normal">Country</h4>
              )}
              <span className="h4 !m-0 !font-normal opacity-66">
                {orderLoading ? (
                  <LoadingText inline={false} size="md" />
                ) : (
                  insight?.country?.label || "!N/A"
                )}
              </span>
            </div>

            <div className="bg-[var(--neutral-10)] p-4 flex-2 flex flex-col gap-3 rounded-xs">
              {orderLoading ? (
                <LoadingText size="lg" inline={false} />
              ) : (
                <h4 className="h3 !m-0 !font-normal">Question</h4>
              )}

              <span className="h4 !m-0 !font-normal opacity-66">
                {orderLoading ? (
                  <LoadingText inline={false} size="md" />
                ) : (
                  insight?.question?.label || "!N/A"
                )}
              </span>
            </div>

            <div className="bg-[var(--neutral-10)] p-4 flex-2 flex flex-col gap-3 rounded-xs">
              {orderLoading ? (
                <LoadingText size="lg" inline={false} />
              ) : (
                <h4 className="h3 !m-0 !font-normal">Timeframe(s)</h4>
              )}
              <span className="h4 !m-0 !font-normal opacity-66">
                {orderLoading ? (
                  <LoadingText inline={false} size="md" />
                ) : Array.isArray(insight?.timeframe) &&
                  insight.timeframe.length > 0 ? (
                  `${insight.timeframe.map((i) => i.label).join(", ")}`
                ) : (
                  "!N/A"
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {orderLoading
              ? Array(3)
                  .fill(0)
                  .map((_, index) => (
                    <Skeleton
                      key={`skeleton-loader-${index}`}
                      className="h-17 w-[15%]"
                    />
                  ))
              : Array.isArray(insight?.audiences) &&
                insight.audiences.map((audience) => (
                  <button
                    key={`audience-${audience.audience_id}`}
                    onClick={() => setSelectedAudienceId(audience.audience_id)}
                    className={`h3 !m-0 !font-normal p-4 cursor-pointer rounded-xs transition-all ${
                      selectedAudienceId === audience.audience_id
                        ? "bg-[var(--primary-300)] text-[var(--neutral-0)] hover:bg-[var(--primary-100)]"
                        : "bg-[var(--neutral-10)] hover:bg-[var(--neutral-30)]"
                    }`}
                  >
                    Audience {audience.audience_index}
                  </button>
                ))}
            {}
          </div>
          <div className="bg-[var(--neutral-10)] p-4 flex-2 flex flex-col gap-3 rounded-xs">
            {orderLoading ? (
              <LoadingText inline={false} size="lg" />
            ) : (
              <h4 className="h3 !m-0 !font-normal">
                Audience {selectedAudience?.audience_index}
              </h4>
            )}

            {orderLoading ? (
              <LoadingText inline={false} size="sm" />
            ) : (
              <h5 className="h5 !m-0">Attributes</h5>
            )}

            <div className="flex flex-wrap gap-y-3 gap-x-10">
              {orderLoading ? (
                Array(4)
                  .fill(0)
                  .map((_, index) => (
                    <LoadingText
                      key={`skeleton-loader-text-${index}`}
                      size="lg"
                      className="w-[20%] !h-15"
                    />
                  ))
              ) : (
                <>
                  {selectedAudience?.age && (
                    <DisplayField
                      label="Age"
                      // value={selectedAudience?.age
                      //   .map((age) => age.label)
                      //   .join(" · ")}
                      value={
                        isArrayWithItems(selectedAudience.age)
                          ? selectedAudience.age
                              ?.map((age) => age.label)
                              .join(" · ")
                          : (selectedAudience.age as { label: string })?.label // July 31, 2025 - Temporarily leaving this option for value in for older json configs that had age as an object
                      }
                    />
                  )}

                  {Array.isArray(selectedAudience?.attributes) &&
                    selectedAudience?.attributes.map((attribute) => (
                      <DisplayField
                        key={attribute?.category_label}
                        label={attribute?.category_label}
                        value={attribute?.levels
                          .map((level) => level?.label)
                          .join(" · ")}
                      />
                    ))}

                  {Array.isArray(selectedAudience?.fandomFilters) &&
                    selectedAudience?.fandomFilters.map((attribute) => (
                      <DisplayField
                        key={attribute?.category_label}
                        label={attribute?.category_label}
                        value={attribute?.levels?.label}
                      />
                    ))}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 px-2">
          <div className="flex flex-col md:flex-row justify-between md:items-end">
            {orderLoading ? (
              <LoadingText inline={false} size="lg" />
            ) : (
              <>
                <h4 className="h3 !m-0 !font-normal">
                  {isCurrentReportLoading ? "Loading Results..." : "Results"}
                </h4>

                <span className="p text-[var(--neutral-400)]">
                  <Info size={12} className="inline" /> Interested in more
                  metrics? Discover more at{" "}
                  <a
                    className="underline"
                    href="https://visioninsights.net/contact"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    visioninsights.net/contact
                  </a>
                </span>
              </>
            )}
          </div>
          {orderLoading ? (
            <Skeleton className="w-full h-[500px]" />
          ) : (
            <div>
              {isCurrentReportLoading ? (
                <Skeleton className="w-full h-[500px]" />
              ) : currentReportMatrix ? (
                <DynamicTable
                  data={currentReportMatrix}
                  columns={[
                    {
                      key: "variable",
                      label: insight?.question?.label || "Variable",
                    },
                    {
                      key: "percent",
                      label: "Percentage",
                      className: "text-right",
                      formatter: (value) => {
                        if (typeof value === "number") {
                          return `${(value * 100).toFixed(0)}%`;
                        }
                        return `${value}%`;
                      },
                    },
                    {
                      key: "sampleSize",
                      label: "Sample Size",
                      className: "text-right",
                      formatter: (value) => {
                        if (typeof value === "number") {
                          return value.toFixed(0);
                        }
                        return value;
                      },
                    },
                  ]}
                />
              ) : (
                <div className="w-full h-[250px] flex items-center justify-center p-6 rounded-xs bg-[var(--neutral-10)]">
                  <h3 className="h4 block text-center !font-normal">
                    <Inbox size={15} className="inline-block" /> No report data
                    available
                  </h3>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InsightDetail;
